import { CheckCircle, Clock } from "@phosphor-icons/react";


export function Loading({ label = "Loading" }) {
  return (
    <div
      className="flex items-center justify-center py-24"
      data-testid="loading-state"
    >
      <div className="flex flex-col items-center gap-3">
        <div className="h-8 w-8 rounded-full border-2 border-muted border-t-primary animate-spin" />
        <p className="eyebrow">{label}</p>
      </div>
    </div>
  );
}

export function PageHeader({ eyebrow, title, description, children }) {
  return (
    <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between mb-8">
      <div className="animate-fade-up">
        {eyebrow && <p className="eyebrow mb-2">{eyebrow}</p>}
        <h1 className="font-heading text-5xl sm:text-6xl font-normal leading-none text-primary">
          {title}
        </h1>
        {description && (
          <p className="text-sm sm:text-base text-muted-foreground mt-3 max-w-2xl">
            {description}
          </p>
        )}
      </div>
      {children && (
        <div className="flex flex-wrap items-center gap-2 shrink-0">
          {children}
        </div>
      )}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, description, action }) {  return (
    <div
      className="flex flex-col items-center justify-center text-center py-20 border border-dashed border-border rounded-xl bg-card/50"
      data-testid="empty-state"
    >
      {Icon && (
        <Icon size={40} weight="light" className="text-secondary mb-4" />
      )}
      <h3 className="font-heading text-2xl text-primary">{title}</h3>
      {description && (
        <p className="text-muted-foreground mt-2 max-w-sm">{description}</p>
      )}
      {action && <div className="mt-6">{action}</div>}
    </div>
  );
}


export function formatAuditTime(value) {
  if (!value) {
    return "Time not recorded";
  }
  return new Date(value).toLocaleString("en-AU", {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function ConfirmationStamp({ label = "Confirmed", at, by, testid }) {
  if (!at) {
    return null;
  }
  return (
    <span
      className="inline-flex flex-wrap items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-3 py-1.5 text-xs text-emerald-900"
      data-testid={testid}
    >
      <CheckCircle size={14} weight="fill" className="text-emerald-600" />
      {label} {formatAuditTime(at)}
      {by ? ` · ${by}` : ""}
    </span>
  );
}

// Pending / Confirmed indicator. Confirmed is green and exposes its audit trail on hover.
export function StatusBadge({
  status,
  at,
  by,
  testid,
  confirmedLabel = "Confirmed",
  pendingLabel = "Pending",
}) {
  const confirmed = status === "confirmed";
  const detail = confirmed
    ? `${confirmedLabel} ${formatAuditTime(at)}${by ? ` · ${by}` : ""}`
    : "Not confirmed yet — this is still a draft.";
  return (
    <span className="group relative inline-flex">
      <span
        title={detail}
        data-testid={testid}
        data-status={confirmed ? "confirmed" : "pending"}
        className={[
          "inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-medium",
          confirmed
            ? "border-emerald-300 bg-emerald-50 text-emerald-800"
            : "border-amber-300 bg-amber-50 text-amber-800",
        ].join(" ")}
      >
        {confirmed ? (
          <CheckCircle size={13} weight="fill" className="text-emerald-600" />
        ) : (
          <Clock size={13} className="text-amber-600" />
        )}
        {confirmed ? confirmedLabel : pendingLabel}
      </span>
      <span
        role="tooltip"
        data-testid={testid ? `${testid}-detail` : undefined}
        className="pointer-events-none absolute right-0 top-full z-30 mt-2 hidden w-max max-w-[16rem] rounded-md border border-border bg-card px-3 py-2 text-left text-xs text-muted-foreground shadow-lg group-hover:block group-focus-within:block"
      >
        {detail}
      </span>
    </span>
  );
}


export function ProgressBar({ value, max, label, testid }) {
  const total = max || 1;
  const percent = Math.min(100, Math.round((value / total) * 100));
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between gap-3">
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="text-sm text-primary" data-testid={testid ? `${testid}-percent` : undefined}>
          {percent}%
        </p>
      </div>
      <div
        className="mt-2 h-2.5 w-full overflow-hidden rounded-full bg-muted"
        role="progressbar"
        aria-valuenow={percent}
        aria-valuemin={0}
        aria-valuemax={100}
        aria-label={label}
      >
        <div
          className="h-full rounded-full bg-secondary transition-[width] duration-700 ease-out"
          style={{ width: `${percent}%` }}
          data-testid={testid ? `${testid}-fill` : undefined}
        />
      </div>
    </div>
  );
}
